import { Box, Switch, Text } from "@dehub/theme";
import type { TFunction } from "i18next";
import classes from "@/features/warehouse/warehouse.module.css";
import type { IWarehouseStaffRole } from "@/apis/warehouseStaff";
import { getRoleLabel } from "../constants";
import partClasses from "./StaffFormParts.module.css";

type RoleCardsProps = Readonly<{
  t: TFunction;
  roles: IWarehouseStaffRole[];
  value: string;
  onChange: (role: IWarehouseStaffRole) => void;
  /** Roles the user may not pick are shown, disabled with the reason, not hidden. */
  disabledReason?: string | null;
  error?: string;
}>;

/** The warehouse role as a row of cards (name + what it can do), instead of a drop-down. */
export function RoleCards({ t, roles, value, onChange, disabledReason, error }: RoleCardsProps) {
  return (
    <Box>
      <Box className={classes.roleCards} role="group" aria-label={t("warehouse.role")}>
        {roles.map((role) => {
          const locked = !!disabledReason;
          return (
            <Box
              key={role}
              component="button"
              type="button"
              className={classes.roleCard}
              aria-pressed={value === role}
              aria-disabled={locked || undefined}
              title={locked ? (disabledReason ?? undefined) : undefined}
              onClick={() => {
                if (!locked) onChange(role);
              }}>
              <span className={classes.roleName}>{getRoleLabel(t, role)}</span>
              <span className={classes.roleSub}>{t(`warehouse.roleSub.${role}`)}</span>
            </Box>
          );
        })}
      </Box>
      {error ? (
        <Text fz={14} c="red.8" mt={4}>
          {error}
        </Text>
      ) : null}
    </Box>
  );
}

type FlagSwitchProps = Readonly<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}>;

/** A special-permission switch with its explanation under the label; the whole row toggles it. */
export function FlagSwitch({ label, description, checked, onChange }: FlagSwitchProps) {
  return (
    <Box component="label" className={partClasses.flagCard} data-checked={checked || undefined}>
      <Box className={partClasses.flagText}>
        <Text fz={14} fw={600}>
          {label}
        </Text>
        <Text fz={14} c="gray.8">
          {description}
        </Text>
      </Box>
      <Switch checked={checked} onChange={(event) => onChange(event.currentTarget.checked)} />
    </Box>
  );
}
